import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr, inrPlain, ddmmyyyy } from "@/lib/format";
import { TrendUp, TrendDown, Wallet, ArrowRight, Bank, Receipt, PaperPlaneTilt } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { useBooks } from "@/context/BookContext";

const Kpi = ({ label, value, sub, icon: Icon, tone = "default", testid }) => {
  const tones = {
    default: "bg-white",
    pos: "bg-[#d1fae5]",
    neg: "bg-[#fee2e2]",
    warn: "bg-[#fef3c7]",
    info: "bg-[#cffafe]",
    teal: "bg-[#2cb4a6] text-white",
    brand: "bg-[#1e2a6b] text-white",
  };
  return (
    <div data-testid={testid} className={`brut-card p-4 ${tones[tone]}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="brut-label" style={tone === "brand" || tone === "teal" ? {color:"#fff"} : {}}>{label}</span>
        <Icon size={22} weight="bold" />
      </div>
      <div className="font-mono-fin font-bold text-xl md:text-2xl leading-tight whitespace-nowrap">{value}</div>
      {sub && <div className="text-xs mt-1 opacity-70">{sub}</div>}
    </div>
  );
};

export default function Dashboard() {
  const { activeId, activeBook } = useBooks();
  const isAK = (activeBook?.name || "").toLowerCase() === "ak accounts";
  const [s, setS] = useState(null);
  const [tl, setTl] = useState([]);
  const [byPerson, setByPerson] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const params = activeId ? { book_id: activeId } : {};
      const [a, b, c, d] = await Promise.all([
        api.get("/summary/dashboard", { params }),
        api.get("/summary/timeline", { params }),
        api.get("/summary/by-person", { params }),
        api.get("/transactions", { params }),
      ]);
      setS(a.data);
      setTl(b.data.map(x => ({ ...x, outflow: Math.abs(x.outflow) })));
      setByPerson(c.data.slice(0, 6));
      setRecent(d.data.slice(0, 6));
    })();
  }, [activeId]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#2cb4a6]">At a glance{activeBook ? ` · ${activeBook.name}` : ""}</div>
          <h1 className="font-display font-black text-4xl md:text-5xl leading-none">Dashboard.</h1>
        </div>
        <Link to="/transactions" className="brut-btn brut-btn-primary flex items-center gap-2" data-testid="add-first-tx">
          Open ledger <ArrowRight size={16} weight="bold" />
        </Link>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <Kpi testid="kpi-cash-balance" label={isAK ? "Balance" : "Cash Balance"} value={inrPlain(s?.cash_balance)} icon={Wallet} tone="brand" />
        <Kpi testid="kpi-cash-inflow" label="Cash Inflow" value={inrPlain(s?.cash_inflow)} icon={TrendUp} tone="pos" />
        <Kpi testid="kpi-cash-outflow" label="Cash Outflow" value={inrPlain(Math.abs(s?.cash_outflow || 0))} icon={TrendDown} tone="neg" />
        <Kpi testid="kpi-upi-outflow" label="UPI Outflow" value={inrPlain(Math.abs(s?.upi_outflow || 0))} sub={`${s?.transactions || 0} entries`} icon={Receipt} tone="info" />
        <Kpi testid="kpi-dtt-outflow" label="DTT Outflow" value={inrPlain(Math.abs(s?.dtt_outflow || 0))} sub={s?.dtt_inflow ? `+${inrPlain(s.dtt_inflow)} in` : ""} icon={PaperPlaneTilt} tone="teal" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="brut-card p-5 xl:col-span-2">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="brut-label">Cash Flow Timeline</div>
              <div className="font-display font-black text-2xl">Inflow vs Outflow</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={tl} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="0" stroke="#1a1a1a20" />
                <XAxis dataKey="date" stroke="#1a1a1a" fontSize={11} tickFormatter={(v) => v?.slice(5)} />
                <YAxis stroke="#1a1a1a" fontSize={11} />
                <Tooltip
                  contentStyle={{ border: "2px solid #1a1a1a", borderRadius: 0, boxShadow: "4px 4px 0 #1a1a1a", background: "#fff", fontFamily: "JetBrains Mono" }}
                />
                <Legend />
                <Line type="linear" dataKey="inflow" stroke="#276749" strokeWidth={3} dot={{ r: 4, fill: "#276749" }} />
                <Line type="linear" dataKey="outflow" stroke="#c53030" strokeWidth={3} dot={{ r: 4, fill: "#c53030" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="brut-card p-5">
          <div className="brut-label mb-1">Top People</div>
          <div className="font-display font-black text-2xl mb-4">By Volume</div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={byPerson} layout="vertical" margin={{ top: 0, right: 10, left: 20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="0" stroke="#1a1a1a20" />
                <XAxis type="number" stroke="#1a1a1a" fontSize={10} />
                <YAxis type="category" dataKey="name" stroke="#1a1a1a" fontSize={11} width={80} />
                <Tooltip contentStyle={{ border: "2px solid #1a1a1a", borderRadius: 0, boxShadow: "4px 4px 0 #1a1a1a", background: "#fff", fontFamily: "JetBrains Mono" }} />
                <Bar dataKey="count" fill="#de5d3e" stroke="#1a1a1a" strokeWidth={2} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent transactions */}
      <div className="brut-card p-0 overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b-2 border-[#1a1a1a]">
          <div>
            <div className="brut-label">Latest activity</div>
            <div className="font-display font-black text-2xl">Recent Transactions</div>
          </div>
          <Link to="/transactions" className="brut-btn brut-btn-secondary text-sm">View all</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="brut" data-testid="dashboard-recent-table">
            <thead>
              <tr>
                <th>Date</th><th>Mode</th><th>Name</th><th className="text-right">Amount</th><th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.id}>
                  <td className="font-mono-fin">{ddmmyyyy(r.date)}</td>
                  <td>
                    <span className={`chip ${r.mode === "cash" ? "chip-cash" : r.mode === "upi" ? "chip-upi" : "chip-dtt"}`}>{r.mode}</span>
                  </td>
                  <td className="font-bold">{r.name}</td>
                  <td className={`text-right font-mono-fin ${r.amount < 0 ? "num-neg" : "num-pos"}`}>{inr(r.amount)}</td>
                  <td className="text-[#5c5c59]">{r.purpose || (r.bank ? `via ${r.bank}` : "—")}</td>
                </tr>
              ))}
              {recent.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-[#5c5c59]">No transactions yet. Head to the ledger to add your first entry.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
